import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Download,
  FileSpreadsheet,
  FileText,
  FilterX,
  Loader2,
  RefreshCw,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { PrzyciskTxt } from '@/components/PrzyciskTxt';
import { useToast } from '@/components/ui/toast';
import {
  buildCsvBlob,
  buildPdfBlob,
  buildXlsxBlob,
  downloadBlob,
  type ExportColumn,
  type ExportRow,
  buildTxtBlob,
  type FormatEksportu,
} from '@/lib/export';
import { claimReport, fetchCooldowns, type ReportKey } from '@/lib/reports-api';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { doEksportu, naEkran } from '@/lib/kolumny-eksportu';
import { cn } from '@/lib/utils';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends unknown, TValue> {
    /** Wartość do eksportu (CSV/Excel/PDF). Kolumny bez tego są w eksporcie pomijane. */
    exportValue?: (row: TData) => string | number | null;
    /**
     * ═════════════════════════════════════════════════════════════════
     *  KOLUMNA, KTÓRA ISTNIEJE WYŁĄCZNIE W EKSPORCIE — na ekranie jej nie ma.
     *
     *  ⚠️⚠️ DOŁOŻONE 09.09.2026 ZE ZGŁOSZENIA: „dodaj w eksporcie pracowników, żeby był też
     *  numer karty i centrum kosztowe (w tej chwili tego brakuje)". Ekran pokazuje te dwie rzeczy
     *  w DRUGIM WIERSZU komórki z nazwiskiem („nr 0001 · alias · CK 666"), a eksport bierze
     *  z kolumny JEDNĄ wartość (`exportValue`) — więc oddawał samo nazwisko.
     *
     *  ⚠️⚠️ DLACZEGO NIE „SKLEIMY TEGO W JEDNĄ KOMÓRKę". Eksport idzie do arkusza, w którym
     *  ludzie filtrują i sortują po numerze karty albo po centrum kosztowym. Trzy rzeczy w jednej
     *  komórce znaczą przepisywanie ich z ręki po drugiej stronie — czyli dokładnie ta praca,
     *  której eksport ma oszczędzać.
     *
     *  ⚠️⚠️ DLACZEGO NIE DOŁOŻYĆ ICH JAKO ZWYKŁYCH KOLUMN. Zgłoszenie dotyczy EKSPORTU; te dane
     *  są na ekranie widoczne od dawna (pod nazwiskiem). Dwie nowe kolumny w tabeli zwężiłyby
     *  pozostałe i pokazywałyby to samo dwa razy — zmiana, o którą nikt nie prosił.
     * ═════════════════════════════════════════════════════════════════
     */
    exportOnly?: boolean;
    align?: 'left' | 'right' | 'center';
    /** Klucz sortowania po stronie API (jeśli inny niż `id` kolumny). */
    sortKey?: string;
  }
}

export type DataTableState = {
  search: string;
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
};

export const PAGE_SIZES = [10, 25, 50, 100] as const;

type Props<T> = {
  title: string;
  /**
   * Pasek pod tytułem, nad wyszukiwarką — np. zakładki strony (Automaty: lista / ustawienia).
   * ⚠️ POD TYTUŁEM, NIE NAD NIM: tytuł obowiązuje w każdej zakładce, więc zostaje na górze.
   */
  podTytulem?: ReactNode;
  columns: ColumnDef<T>[];
  data: T[];
  total: number;
  state: DataTableState;
  onStateChange: (patch: Partial<DataTableState>) => void;
  isLoading?: boolean;
  isFetching?: boolean;
  onRefresh?: () => void;
  /** Przyciski akcji obok toolbara (np. „Dodaj"). */
  actions?: ReactNode;
  /** Zawartość wysuwanego panelu filtrów zaawansowanych. */
  filters?: ReactNode;
  /**
   * NAZWY CZYNNYCH FILTRÓW — pasek nad tabelą i licznik przy przycisku „Filtry".
   *
   * ⚠️⚠️ ZGŁOSZONE 01.09.2026 (artykuły): „przy włączonym filtrze powinno być info, że jest
   * zastosowany filtr i prosty przycisk do usunięcia filtrów, bo teraz trzeba wejść w inną
   * zakładkę i wrócić, żeby wyczyścić filtry". Panel filtrów jest ZWINIĘTY, dopóki ktoś go nie
   * rozwinie, więc filtr ustawiony gdzie indziej (odnośnik z grup towarowych) przycinał listę do
   * jednego wiersza i nie było na ekranie ani śladu, dlaczego.
   *
   * Ekran podaje gotowe napisy, bo tylko on wie, co znaczy jego własny filtr po polsku.
   */
  aktywneFiltry?: string[];
  onWyczyscFiltry?: () => void;
  exportFileName: string;
  /**
   * Eksport pełnego zbioru (nie tylko widocznej strony). Bez tego eksportujemy stronę.
   *
   * ⚠️ Dostaje FORMAT, bo limit jest różny dla każdego (`LIMIT_EKSPORTU`): CSV zniesie 250 tys.
   * wierszy, a XLSX nie — `exceljs` buduje w pamięci obiekt na każdą komórkę. Bez formatu strona
   * musiałaby przyjąć jedną liczbę dla wszystkich, czyli najniższą — i tak powstał limit 20 000,
   * który dławił CSV bez żadnego powodu.
   *
   * `postep` woła się po każdej stronie; służy licznikowi przy przycisku, bo eksport dużego
   * raportu to kilkadziesiąt żądań i samo kręcące się kółko wygląda jak zawieszenie.
   */
  fetchAllRows?: (format: FormatEksportu, postep?: (ile: number, zIlu: number) => void) => Promise<T[]>;
  /**
   * Klucz raportu dla COOLDOWNU (PLAN §8a). Bez niego eksport działa bez ograniczeń —
   * to celowe: listy, które nie ciągną całego zbioru z bazy, nie mają czego dławić.
   */
  reportKey?: ReportKey;
  /** wyspa, której dotyczy raport — cooldown liczy się osobno dla każdej */
  reportGroupId?: string | null;
  emptyText?: string;
  rowKey: (row: T) => string;
};

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function DataTable<T>({
  title,
  podTytulem,
  columns,
  data,
  total,
  state,
  onStateChange,
  isLoading,
  isFetching,
  onRefresh,
  actions,
  filters,
  aktywneFiltry,
  onWyczyscFiltry,
  exportFileName,
  fetchAllRows,
  reportKey,
  reportGroupId,
  emptyText,
  rowKey,
}: Props<T>) {
  const { t } = useI18n();
  const toast = useToast();

  const [searchInput, setSearchInput] = useState(state.search);
  const debouncedSearch = useDebounced(searchInput);
  const lastSent = useRef(state.search);
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState<null | FormatEksportu>(null);
  /** `[zebrane, wszystkich]` w trakcie eksportu — `null`, gdy nie ma czego pokazywać. */
  const [postep, setPostep] = useState<[number, number] | null>(null);
  const setPostepZ = useCallback((ile: number, zIlu: number) => setPostep([ile, zIlu]), []);

  /**
   * Ile sekund zostało do następnego eksportu. Pobierane Z SERWERA przy wejściu na listę,
   * a nie tylko trzymane po kliknięciu — bez tego wystarczyłoby odświeżyć stronę, żeby
   * zobaczyć aktywny przycisk, który serwer i tak odrzuci. Obietnica interfejsu ma się
   * zgadzać z tym, co zrobi serwer.
   */
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!reportKey) return;
    let alive = true;
    void fetchCooldowns(reportGroupId)
      .then((d) => {
        if (alive) setCooldown(d.remaining[reportKey] ?? 0);
      })
      // cooldown to wygoda, nie funkcja krytyczna — jego nieodczytanie nie ma prawa
      // zepsuć listy; serwer i tak odmówi, jeśli będzie za wcześnie
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [reportKey, reportGroupId]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  useEffect(() => {
    if (debouncedSearch !== lastSent.current) {
      lastSent.current = debouncedSearch;
      onStateChange({ search: debouncedSearch, page: 1 });
    }
  }, [debouncedSearch, onStateChange]);

  /**
   * Szukanie ustawione Z ZEWNĄTRZ musi wrócić do pola.
   *
   * `searchInput` brał `state.search` tylko przy montowaniu, więc gdy strona czyściła szukanie
   * sama (przycisk „wyczyść filtry"), pole **nadal pokazywało stary tekst**, a lista była już
   * nieprzefiltrowana — UI twierdził co innego, niż robił. Warunek na `lastSent` jest konieczny:
   * bez niego ten efekt cofałby literę, którą użytkownik właśnie wpisał, bo `state.search`
   * dogania pole dopiero po debounce.
   */
  useEffect(() => {
    if (state.search !== lastSent.current) {
      lastSent.current = state.search;
      setSearchInput(state.search);
    }
  }, [state.search]);

  /**
   * ⚠️⚠️ TABELA RYSUJE KOLUMNY WIDOCZNE, EKSPORT BIERZE WSZYSTKIE. Filtr jest tutaj, a nie
   * u wołającego, bo `useReactTable` rysuje wszystko, co dostanie — kolumna „tylko do eksportu"
   * przekazana wprost pojawiłaby się na ekranie jako pusty słupek z nagłówkiem.
   */
  const kolumnyWidoczne = useMemo(() => naEkran(columns), [columns]);

  const table = useReactTable({
    data,
    columns: kolumnyWidoczne,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: Math.max(1, Math.ceil(total / state.pageSize)),
  });

  const pageCount = Math.max(1, Math.ceil(total / state.pageSize));
  const from = total === 0 ? 0 : (state.page - 1) * state.pageSize + 1;
  const to = Math.min(total, state.page * state.pageSize);

  const toggleSort = (colId: string, sortKey?: string) => {
    const key = sortKey ?? colId;
    if (state.sort === key) onStateChange({ dir: state.dir === 'asc' ? 'desc' : 'asc' });
    else onStateChange({ sort: key, dir: 'asc' });
  };

  const exportColumnsAndRows = async (
    format: FormatEksportu,
  ): Promise<{ cols: ExportColumn[]; rows: ExportRow[] }> => {
    const exportable = doEksportu(columns);
    const cols: ExportColumn[] = exportable.map((c, i) => ({
      key: String(c.id ?? i),
      header: typeof c.header === 'string' ? c.header : String(c.id ?? i),
    }));

    const source = fetchAllRows ? await fetchAllRows(format, setPostepZ) : data;
    const rows: ExportRow[] = source.map((row) =>
      Object.fromEntries(exportable.map((c, i) => [String(c.id ?? i), c.meta!.exportValue!(row)])),
    );
    return { cols, rows };
  };

  /**
   * ⚠️ `naglowki` DOTYCZY WYŁĄCZNIE `txt` i przychodzi Z MINI LISTY pod przyciskiem TXT
   * (`PrzyciskTxt`), a nie z ustawienia wyspy — patrz uzasadnienie w tamtym komponencie.
   * Dla pozostałych formatów nagłówek jest zawsze, więc parametr jest tam bez znaczenia.
   */
  const runExport = async (kind: FormatEksportu, naglowki = true) => {
    setExporting(kind);
    setPostep(null);
    try {
      /**
       * Zajęcie okna PRZED pobraniem danych. Eksport przewija cały zbiór stronami, więc
       * jedno kliknięcie to kilkanaście zapytań do bazy — pytamy o zgodę, zanim je wyślemy,
       * a nie po fakcie.
       */
      if (reportKey) {
        try {
          const c = await claimReport(reportKey, reportGroupId);
          setCooldown(c.cooldownSeconds);
        } catch (err) {
          if (err instanceof ApiError && err.status === 429) {
            toast.error(t('table.cooldownWait'));
            // odczytujemy pozostały czas z serwera zamiast zgadywać
            void fetchCooldowns(reportGroupId)
              .then((d) => setCooldown(d.remaining[reportKey] ?? 0))
              .catch(() => undefined);
            return;
          }
          throw err;
        }
      }

      const { cols, rows } = await exportColumnsAndRows(kind);
      if (rows.length === 0) {
        toast.error(t('table.exportEmpty'));
        return;
      }
      if (kind === 'csv') downloadBlob(buildCsvBlob(cols, rows), `${exportFileName}.csv`);
      if (kind === 'txt') downloadBlob(buildTxtBlob(cols, rows, naglowki), `${exportFileName}.txt`);
      if (kind === 'xlsx') downloadBlob(await buildXlsxBlob(cols, rows, title), `${exportFileName}.xlsx`);
      if (kind === 'pdf') downloadBlob(await buildPdfBlob(cols, rows, title), `${exportFileName}.pdf`);
      toast.success(t('table.exportDone'));
    } catch (err) {
      toast.error(`${t('table.exportFailed')}: ${(err as Error).message}`);
    } finally {
      setExporting(null);
      setPostep(null);
    }
  };

  const czynneFiltry = aktywneFiltry ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        </div>
        <div className="flex items-center gap-2">{actions}</div>
      </div>

      {podTytulem}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('table.search')}
            aria-label={t('table.search')}
            className="pl-8"
          />
        </div>

        {filters && (
          <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters}>
            <SlidersHorizontal />
            {t('table.filters')}
            {/* Licznik NA przycisku, bo przy zwiniętym panelu to jedyne miejsce, które go widzi. */}
            {czynneFiltry.length > 0 && (
              <span className="rounded bg-primary/15 px-1.5 text-[11px] font-medium tabular-nums text-primary">
                {czynneFiltry.length}
              </span>
            )}
          </Button>
        )}

        {onRefresh && (
          <Button variant="outline" size="sm" onClick={onRefresh} title={t('table.refresh')} aria-label={t('table.refresh')}>
            <RefreshCw className={cn(isFetching && 'animate-spin')} />
          </Button>
        )}

        {/*
          Licznik pobranych wierszy. Duży raport to kilkadziesiąt żądań i kilkanaście sekund —
          bez tego jedyną informacją zwrotną jest kręcące się kółko, nieodróżnialne od zawieszenia.
          Pokazuje się dopiero, gdy zbiór nie zmieścił się w jednej stronie: przy krótkich listach
          mignięcie licznika byłoby samym hałasem.
        */}
        {postep !== null && postep[0] < postep[1] && (
          <span className="text-xs tabular-nums text-[var(--muted-foreground)]" data-export-progress>
            {postep[0].toLocaleString('pl-PL')} / {postep[1].toLocaleString('pl-PL')}
          </span>
        )}

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void runExport('csv')}
            disabled={exporting !== null || cooldown > 0}
            data-export="csv"
            title={cooldown > 0 ? `${t('table.cooldownIn')} ${cooldown} s` : 'CSV'}
          >
            {exporting === 'csv' ? <Loader2 className="animate-spin" /> : <Download />}
            CSV
          </Button>
          {/*
            ⚠️ TXT OBOK CSV, NIE ZAMIAST — to plik dla PROGRAMU, który go zaciąga (zgłoszone
            31.08.2026: „MJM ma program, gdzie zaciągają txt tab delimitowany"). Ma inne reguły niż
            CSV: tabulator zamiast średnika, bez BOM, i opcjonalnie bez wiersza nagłówków.

            ⚠️ KOLEJNOŚĆ FORMATÓW: CSV, TXT, XLSX, PDF — ta sama co w oknie „własne kolumny"
            (zgłoszone 31.08.2026: „trzeba ujednolicić kolejność i nazwy plików"). Dwa paski
            z tymi samymi przyciskami w innej kolejności to najprostszy sposób, żeby ktoś kliknął
            nie ten format.

            ⚠️ NAGŁÓWKI WYBIERA SIĘ TU, przy pobieraniu, a nie w Ustawieniach wyspy — całe
            uzasadnienie w `PrzyciskTxt`.
          */}
          <PrzyciskTxt
            disabled={exporting !== null || cooldown > 0}
            pracuje={exporting === 'txt'}
            tytul={cooldown > 0 ? `${t('table.cooldownIn')} ${cooldown} s` : undefined}
            onWybor={(naglowki) => void runExport('txt', naglowki)}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => void runExport('xlsx')}
            disabled={exporting !== null || cooldown > 0}
            data-export="xlsx"
            title={cooldown > 0 ? `${t('table.cooldownIn')} ${cooldown} s` : 'XLSX'}
          >
            {exporting === 'xlsx' ? <Loader2 className="animate-spin" /> : <FileSpreadsheet />}
            XLSX
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void runExport('pdf')}
            disabled={exporting !== null || cooldown > 0}
            data-export="pdf"
            title={cooldown > 0 ? `${t('table.cooldownIn')} ${cooldown} s` : 'PDF'}
          >
            {exporting === 'pdf' ? <Loader2 className="animate-spin" /> : <FileText />}
            PDF
          </Button>
        </div>
      </div>

      {czynneFiltry.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
          <span className="text-xs text-muted-foreground">{t('table.filtersActive')}</span>
          {czynneFiltry.map((f) => (
            <span key={f} className="rounded border border-primary/30 bg-background px-2 py-0.5 text-xs">
              {f}
            </span>
          ))}
          {onWyczyscFiltry && (
            <Button variant="outline" size="sm" className="ml-auto h-7" onClick={onWyczyscFiltry}>
              <FilterX className="size-3.5" />
              {t('table.clearFilters')}
            </Button>
          )}
        </div>
      )}

      {filters && showFilters && (
        <div className="rounded-lg border border-border bg-muted/30 p-4">{filters}</div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[40rem] border-collapse text-sm">
          <thead className="bg-muted/50">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => {
                  const meta = h.column.columnDef.meta;
                  const sortKey = meta?.sortKey ?? h.column.id;
                  const sortable = h.column.columnDef.enableSorting !== false;
                  const active = state.sort === sortKey;
                  return (
                    <th
                      key={h.id}
                      scope="col"
                      className={cn(
                        'whitespace-nowrap border-b border-border px-3 py-2.5 font-medium text-muted-foreground',
                        meta?.align === 'right' ? 'text-right' : meta?.align === 'center' ? 'text-center' : 'text-left',
                      )}
                    >
                      {sortable ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(h.column.id, meta?.sortKey)}
                          className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {active &&
                            (state.dir === 'asc' ? <ArrowUp className="size-3.5" /> : <ArrowDown className="size-3.5" />)}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={kolumnyWidoczne.length} className="px-3 py-10 text-center text-muted-foreground">
                  <Loader2 className="mx-auto mb-2 size-4 animate-spin" />
                  {t('common.loading')}
                </td>
              </tr>
            )}

            {!isLoading && data.length === 0 && (
              <tr>
                <td colSpan={kolumnyWidoczne.length} className="px-3 py-10 text-center text-muted-foreground">
                  {emptyText ?? t('table.empty')}
                </td>
              </tr>
            )}

            {!isLoading &&
              table.getRowModel().rows.map((row, i) => (
                <tr
                  key={rowKey(row.original)}
                  className={cn(
                    'border-b border-border/60 transition-colors last:border-0 hover:bg-accent/60',
                    i % 2 === 1 && 'bg-muted/25',
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta;
                    return (
                      <td
                        key={cell.id}
                        className={cn(
                          'px-3 py-2.5',
                          meta?.align === 'right'
                            ? 'text-right'
                            : meta?.align === 'center'
                              ? 'text-center'
                              : 'text-left',
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <span>
            {t('table.showing')} <span className="font-medium text-foreground">{from}</span>–
            <span className="font-medium text-foreground">{to}</span> {t('table.of')}{' '}
            <span className="font-medium text-foreground">{total}</span>
          </span>
          <Select
            value={String(state.pageSize)}
            onChange={(e) => onStateChange({ pageSize: Number(e.target.value), page: 1 })}
            aria-label={t('table.pageSize')}
            className="h-8 w-20"
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={state.page <= 1}
            onClick={() => onStateChange({ page: state.page - 1 })}
            aria-label={t('table.prev')}
          >
            <ChevronLeft />
          </Button>
          <span>
            {state.page} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={state.page >= pageCount}
            onClick={() => onStateChange({ page: state.page + 1 })}
            aria-label={t('table.next')}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
